import { typedQueryClient, useTypedMutation } from "hooks";
import { APPOINTMENT_NOTE_TYPES } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { toISOString } from "utils/dateHelperFunctions";

import type { AppointmentNoteFields } from "modules/AppointmentDetails/components/AppointmentNoteModal/hooks";

type UpdateAppointmentNotePayload = {
  appointment_note_id: number;
  fields: AppointmentNoteFields;
  request: NonNullable<ReturnType<typeof generateUpdateAppointmentNoteRequest>>;
};

export const generateUpdateAppointmentNoteRequest = (type: APPOINTMENT_NOTE_TYPES, appointment_note_id: number, fields: AppointmentNoteFields) => {
  const { appointment_id, note, attachments, visible_to_mechanic, phone_nr, email, name } = fields;
  const basePayload = { appointment_note_id, appointment_id, note, updated_on: toISOString(new Date()) };

  const requestByType: Record<APPOINTMENT_NOTE_TYPES, { path: string; payload: object } | null> = {
    [APPOINTMENT_NOTE_TYPES.WO]: { path: "/notes/wo/update", payload: { ...basePayload, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.Main]: { path: "/notes/main/update", payload: { ...basePayload, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.CallCustomer]: { path: "/notes/call/update", payload: { ...basePayload, phone_nr, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.Bill]: { path: "/notes/bill/update", payload: { ...basePayload, attachment: attachments?.[0] } },
    [APPOINTMENT_NOTE_TYPES.TempDriver]: { path: "/notes/temp_driver/update", payload: { ...basePayload, name, email, phone_nr, attachments } },
    [APPOINTMENT_NOTE_TYPES.RecurringCar]: { path: "/notes/recurring_car/update", payload: { ...basePayload, attachments } },
    [APPOINTMENT_NOTE_TYPES.Attachment]: { path: "/notes/attachment/update", payload: { ...basePayload, attachments } },
    [APPOINTMENT_NOTE_TYPES.ParkingLocation]: { path: "/notes/parking/update", payload: basePayload },
    [APPOINTMENT_NOTE_TYPES.NoDrive]: { path: "/notes/no_drive/update", payload: basePayload },
    [APPOINTMENT_NOTE_TYPES.Info]: null,
    [APPOINTMENT_NOTE_TYPES.BackOrder]: null,
    [APPOINTMENT_NOTE_TYPES.MakeAnOffer]: null,
    [APPOINTMENT_NOTE_TYPES.Tyrescanner]: null,
    [APPOINTMENT_NOTE_TYPES.DMS]: null,
    [APPOINTMENT_NOTE_TYPES.ReplacementVehicle]: null,
    [APPOINTMENT_NOTE_TYPES.HighVoltageWarning]: null,
    [APPOINTMENT_NOTE_TYPES.XentryAttachment]: null
  };

  return requestByType[type];
};

export const useUpdateAppointmentNoteMutation = () =>
  useTypedMutation({
    mutationFn: async ({ request }: UpdateAppointmentNotePayload) => {
      await ApiInstance.post(request.path, request.payload);
    },
    onSuccess: ({ appointment_note_id, fields }) => {
      const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (!appointment?.notes) return;

      const noteIdx = appointment.notes.findIndex(note => note.id === appointment_note_id);
      if (noteIdx < 0) return;

      typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
        ...appointment,
        notes: appointment.notes.with(noteIdx, { ...appointment.notes[noteIdx], ...fields, updated_on: toISOString(new Date()) })
      });
    }
  });
